/**
 * The jet's twin cannons (GDD §4.2; concept L815–L839, L1288–L1295): a pair of tracers every 0.09 s from the
 * wing roots at 300 m/s plus the jet's speed, each living 0.9 s. Tracers sweep along their path each step, so a fast
 * tracer can't skip through a target: every enemy dies from one hit, a bridge needs 6 + level.
 */
import { config } from '../core/config.ts';
import { localToWorld, quatFromEulerYXZ, rotateByQuat, type Quat4, type Vec3 } from '../core/quat.ts';
import type { GameSim } from '../sim/context.ts';
import type { Tracer } from '../sim/state.ts';
import { killEnemy } from '../entities/enemies.ts';
import { hitBridge } from '../entities/bridge.ts';

/** Muzzles on the jet (concept L1292). */
const MUZZLE_X = 0.7;
const MUZZLE_Y = -0.3;
const MUZZLE_Z = -9;
/** Extra width a tracer hits with (concept L831) and the bridge's depth for hits (L834). */
const HIT_MARGIN = 0.8;
const BRIDGE_DEPTH = 6;

/** Player tracers in the ring: 24 under GDD rules (D14), the concept's 18 for parity. */
export function tracerCount(rules: 'concept' | 'gdd'): number {
  return rules === 'gdd' ? 24 : 18;
}

const q: Quat4 = [0, 0, 0, 1];
const fwd: Vec3 = [0, 0, 0];
const muzzle: Vec3 = [0, 0, 0];

function fireTracer(sim: GameSim, speed: number): void {
  const n = sim.tracers.length;
  const tr = sim.tracers[sim.guns.next++ % n] as Tracer;
  tr.x = tr.prevX = muzzle[0];
  tr.y = tr.prevY = muzzle[1];
  tr.z = tr.prevZ = muzzle[2];
  tr.vx = fwd[0] * speed;
  tr.vy = fwd[1] * speed;
  tr.vz = fwd[2] * speed;
  tr.life = config.guns.tracerLife;
  tr.visible = true;
}

/** Fires a volley when `fire` is held and the guns are ready (concept L1289–L1294). */
export function stepGuns(sim: GameSim, fire: boolean, dt: number): void {
  const g = sim.guns;
  g.fireT -= dt;
  // A lost jet can't shoot. (Concept rules keep the concept's quirk: the autopilot decided to fire before this
  // step's crash, so the wreck fires one last volley.)
  if (!fire || g.fireT > 0 || (sim.rules === 'gdd' && !sim.run.alive)) return;
  const jet = sim.jet;
  quatFromEulerYXZ(jet.pitch, jet.yaw, jet.roll, q);
  rotateByQuat(q, 0, 0, -1, fwd);
  const speed = config.guns.tracerSpeedBonus + jet.speed;
  localToWorld(q, jet.x, jet.y, jet.z, -MUZZLE_X, MUZZLE_Y, MUZZLE_Z, muzzle);
  fireTracer(sim, speed);
  localToWorld(q, jet.x, jet.y, jet.z, MUZZLE_X, MUZZLE_Y, MUZZLE_Z, muzzle);
  fireTracer(sim, speed);
  g.burst++;
  // GDD rules keep the 0.09 s interval exactly (judgment call 25): the timer carries what the step overshot, so at
  // 60 Hz volleys come 5 or 6 steps apart. The concept reset it, which made 0.1 s at 60 frames a second.
  if (sim.rules === 'gdd') g.fireT = (g.fireT < -dt ? 0 : g.fireT) + config.guns.interval;
  else g.fireT = config.guns.interval;
}

/** concept updTracers (L822–L839): tracers fly and hit; `shift` is this step's floating-origin shift. */
export function stepTracers(sim: GameSim, dt: number, shift: number): void {
  const b = sim.bridge;
  const ents = sim.ents;
  for (let i = 0; i < sim.tracers.length; i++) {
    const tr = sim.tracers[i] as Tracer;
    if (tr.life <= 0) continue;
    const pz = tr.z + shift;
    tr.life -= dt;
    tr.prevX = tr.x;
    tr.prevY = tr.y;
    tr.prevZ = pz;
    tr.x += tr.vx * dt;
    tr.y += tr.vy * dt;
    tr.z += tr.vz * dt;
    if (shift) tr.z += shift;
    const x = tr.x;
    const nz = tr.z;
    let hit = false;
    for (let k = 0; k < ents.count && !hit; k++) {
      const e = ents.items[k];
      if (!e || e.dying > 0) continue;
      if (Math.abs(x - e.x) < e.hw + HIT_MARGIN && nz < e.z + e.hl && pz > e.z - e.hl) {
        killEnemy(sim, e);
        hit = true;
      }
    }
    if (!hit && b.intact && nz < b.z + BRIDGE_DEPTH && pz > b.z - BRIDGE_DEPTH && Math.abs(x - b.rootX) < b.span / 2) {
      hitBridge(sim, x);
      hit = true;
    }
    if (hit) tr.life = 0;
    if (tr.life <= 0) tr.visible = false;
  }
}
